import { DialogM } from "@common/components/alert/dialog/Dialog"
import { theme } from "@common/theme/theme"
import { DimensionsContext, IS_WEB } from "@common/utils/mobileUtils"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { formatMinutes, readableDayTime } from "@data/utility/Utils"
import moment from "moment"
import React, { useContext, useRef } from "react"
import { useController } from "react-hook-form"
import { Keyboard, View } from "react-native"
import { ScrollView } from "react-native-gesture-handler"
import { Checkbox } from "react-native-paper"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import FilledButtonM from "../../common/components/button/FilledButtonM"
import TextInputM from "../../common/components/input/TextInputM"
import TextM from "../../common/components/text/TextM"
import { findClosestNextOccurrence } from "../../student/account/onboarding/LiveRoomCard"
import { SelectInputM } from "../../student/account/onboarding/SelectInputM"

export const delayOptions = [
    {
        label: "5 minutes",
        value: 5,
    },
    {
        label: "10 minutes",
        value: 10,
    },
    {
        label: "15 minutes",
        value: 15,
    },
]

export const importantWarning = (
    messageComponent,
    style,
    title = "IMPORTANT"
) => (
    <View
        style={{
            backgroundColor: theme.colors.material.secondary["50"],
            borderRadius: 20,
            borderWidth: 1,
            borderColor: theme.colors.error,
            padding: 15,
            alignItems: "flex-start",
            justifyContent: "flex-start",
            alignSelf: "center",
            width: "100%",
            ...style,
        }}
    >
        <View
            style={{
                flexDirection: "row",
                alignItems: "flex-start",
                marginBottom: 15,
            }}
        >
            <FontAwesome5
                name={"exclamation-triangle"}
                solid
                size={20}
                color={theme.colors.error}
            />
            <TextM
                fontWeight={"ExtraBold"}
                fontSize={18}
                color={theme.colors.error}
                style={{ marginLeft: 10 }}
            >
                {title}
            </TextM>
        </View>
        {messageComponent}
    </View>
)

export const informationWarning = (
    messageComponent,
    style,
    title = "ASTUCE"
) => (
    <View
        style={{
            backgroundColor: theme.colors.material.primary["50"],
            borderRadius: 20,
            padding: 15,
            alignItems: "flex-start",
            justifyContent: "flex-start",
            alignSelf: "center",
            ...style,
        }}
    >
        <View
            style={{
                flexDirection: "row",
                alignItems: "flex-start",
                marginBottom: 15,
            }}
        >
            <FontAwesome5
                name={"info-circle"}
                solid
                size={20}
                color={theme.colors.primary}
            />
            <TextM
                fontWeight={"ExtraBold"}
                fontSize={18}
                color={theme.colors.primary}
                style={{ marginLeft: 10 }}
            >
                {title}
            </TextM>
        </View>
        {messageComponent}
    </View>
)

const MoveSessionReasonComponent = (props) => {
    const {
        liveSession,
        control,
        goToNextStep,
        errors,
        setFormValues,
        shouldSkipMakeupSteps,
    } = props

    const dimensions = useContext(DimensionsContext)
    const isOneOccurrence = control?._formValues?.type === "one_occurrence"
    const closestNextOccurrence = findClosestNextOccurrence([liveSession])
    const firstStudent = liveSession?.students?.[0]?.display_name ?? "l'élève"
    const liveMoment = closestNextOccurrence?.date
        ? moment(closestNextOccurrence?.date)
        : null

    const isIndividual = liveSession?.live_room?.type === "individual"

    // Determine if this is the final step (no makeup sessions steps after this)
    const isFinalStep =
        !isIndividual &&
        control?._formValues?.type === "absence" &&
        shouldSkipMakeupSteps

    const reasons = !isIndividual
        ? props.tutor_absence_reasons
        : isOneOccurrence
        ? props.rescheduling_reasons?.["one_occurrence"]
        : props.rescheduling_reasons?.["all_occurrences"]

    const oldDate = isOneOccurrence
        ? liveMoment.format("dddd DD MMMM [à] HH:mm") +
          " - " +
          formatMinutes(liveSession?.duration)
        : readableDayTime(liveSession?.day_time, liveSession?.duration)

    const newDate = isOneOccurrence
        ? moment(control?._formValues?.new_date)?.format(
              "dddd DD MMMM [à] HH:mm"
          ) +
          " - " +
          formatMinutes(liveSession?.duration)
        : readableDayTime(control?._formValues?.new_date, liveSession?.duration)

    const { field: studentValidation } = useController({
        name: "student_validation",
        control,
    })

    const { field: parentNotificationValidation } = useController({
        name: "parent_notification_validation",
        control,
        defaultValue: false,
    })

    const isMessageRequired =
        !isIndividual ||
        control?._formValues?.reason === "Autre raison (préciser)"

    // Validation spéciale pour les messages aux parents lors d'absence (minimum 50 caractères)
    const isParentMessageValid =
        control?._formValues?.type === "absence" && !isIndividual
            ? control?._formValues?.tutor_message &&
              control?._formValues?.tutor_message.length >= 50
            : true

    const cantContinue = isIndividual
        ? !studentValidation.value ||
          !control?._formValues?.reason ||
          (isMessageRequired &&
              (control?._formValues?.message_to_parent === "" ||
                  !control?._formValues?.message_to_parent))
        : // Pour sessions non-individuelles
          !control?._formValues?.reason ||
          !control?._formValues?.instructions ||
          control?._formValues?.instructions === "" ||
          !control?._formValues?.tutor_message ||
          control?._formValues?.tutor_message === "" ||
          !isParentMessageValid ||
          (control?._formValues?.type === "absence" &&
              !parentNotificationValidation.value)

    const instructionsRef = useRef()

    if (
        !isIndividual &&
        control?._formValues?.late_minutes !== "more" &&
        control?._formValues?.type !== "absence"
    ) {
        return (
            <View
                style={{
                    marginTop: 20,
                    flex: 1,
                }}
            >
                <TextM fontWeight={"Bold"}>
                    Retard de{" "}
                    {
                        delayOptions?.find(
                            (d) =>
                                d?.value === control?._formValues?.late_minutes
                        )?.label
                    }
                    .
                </TextM>
                {importantWarning(
                    <>
                        <TextM
                            wrap
                            style={{
                                marginBottom: 20,
                            }}
                        >
                            C'est bien de prévenir quand un retard est
                            inévitable, mais il ne faut pas que cela se répète !
                        </TextM>
                        <TextM
                            wrap
                            fontWeight={"SemiBold"}
                            style={{
                                marginBottom: 20,
                            }}
                        >
                            Merci de vérifier que chaque élève a des activités
                            pertinentes à faire en attendant ton arrivée.
                        </TextM>
                        <TextM fontWeight={"SemiBold"} wrap>
                            Il faudra compenser ce retard en fin de séance !
                        </TextM>
                    </>
                )}
                <FilledButtonM
                    label={"Confirmer"}
                    color={theme.colors.success}
                    style={{ marginTop: 40 }}
                    onPress={() => {
                        DialogM.show({
                            text1: "Les élèves recevront un message à l'heure prévue de début du live, pour les prévenir de ton retard, et pour qu'ils commencent à travailler.",
                            buttonText1: "Confirmer",
                            buttonColor1: theme.colors.error,
                            onPress: () => {
                                goToNextStep()
                            },
                            buttonText2: "Annuler",
                        })
                    }}
                />
            </View>
        )
    }
    return (
        <>
            <ScrollView
                style={{
                    width: "100%",
                    alignSelf: "center",
                    paddingBottom: 300,
                }}
            >
                {isIndividual && (
                    <SelectInputM
                        options={reasons.map((r) => {
                            return {
                                label: r,
                                value: r,
                            }
                        })}
                        name={"reason"}
                        label={"Raison"}
                        sliderLabel={"Raison"}
                        onSelect={(value) => {
                            setFormValues({
                                ...control?._formValues,
                                reason: value,
                            })
                        }}
                        control={control}
                        style={{
                            width: "100%",
                        }}
                        errors={errors}
                    />
                )}
                {!isIndividual &&
                    control?._formValues?.type === "absence" &&
                    importantWarning(
                        <TextM
                            wrap
                            style={{
                                marginBottom: 15,
                            }}
                        >
                            <TextM fontWeight={"SemiBold"}>
                                Ton absence impacte directement les élèves{" "}
                            </TextM>
                            qui devront s'adapter au masteur remplaçant. Explique
                            clairement la raison de ton absence aux
                            parents/élèves pour maintenir leur confiance.
                        </TextM>,
                        { marginTop: 20, marginBottom: 15 }
                    )}
                <TextM
                    color={theme.colors.primary}
                    fontWeight={"Medium"}
                    fontSize={12}
                    style={{ marginTop: 20 }}
                >
                    {"Écris un message aux " +
                        (isIndividual
                            ? "parents/élève "
                            : control?._formValues?.type === "absence"
                            ? "parents/élèves"
                            : "coordinateur") +
                        (isMessageRequired ? "*" : "")}
                </TextM>
                <TextInputM
                    placeholder={
                        isIndividual
                            ? "Comme discuté ensemble, je propose de déplacer notre séance en raison de ..."
                            : control?._formValues?.type === "absence"
                            ? "Bonjour, je vous présente mes excuses mais je dois m'absenter pour cette séance en raison de..."
                            : "Bonjour, je dois m'absenter sur ce créneau en raison de..."
                    }
                    name={isIndividual ? "message_to_parent" : "tutor_message"}
                    style={{
                        width: IS_WEB
                            ? dimensions?.width - 60
                            : dimensions?.width - 40,
                        height: 90,
                        backgroundColor: theme.colors.white,
                        alignSelf: "center",
                        fontSize: 14,
                    }}
                    multiline={true}
                    blurOnSubmit={true}
                    onChangeAdditional={(value) => {
                        setFormValues({
                            ...control?._formValues,
                            [isIndividual
                                ? "message_to_parent"
                                : "tutor_message"]: value,
                        })
                    }}
                    returnKeyType={!isIndividual ? "next" : "done"}
                    onSubmitEditing={() => {
                        if (!isIndividual) {
                            instructionsRef.current?.focus()
                        } else {
                            Keyboard.dismiss()
                        }
                    }}
                    control={control}
                    errors={errors}
                />
                {!isIndividual && control?._formValues?.type === "absence" && (
                    <TextM
                        fontSize={12}
                        color={
                            control?._formValues?.tutor_message &&
                            control?._formValues?.tutor_message.length >= 50
                                ? theme.colors.success
                                : theme.colors.grey
                        }
                        style={{
                            marginTop: 5,
                            alignSelf: "flex-start",
                        }}
                    >
                        {control?._formValues?.tutor_message
                            ? control?._formValues?.tutor_message.length
                            : 0}
                        /50 caractères minimum requis
                    </TextM>
                )}
                {!isIndividual && (
                    <>
                        <TextM
                            color={theme.colors.primary}
                            fontWeight={"Medium"}
                            fontSize={12}
                            style={{ marginTop: 20 }}
                        >
                            {"Consignes pour le remplaçant*"}
                        </TextM>
                        <TextInputM
                            placeholder={`On a travaillé dernièrement avec ${firstStudent} sur... Bien faire attention à ...`}
                            name={"instructions"}
                            ref={instructionsRef}
                            returnKeyType={"done"}
                            style={{
                                width: IS_WEB
                                    ? dimensions?.width - 60
                                    : dimensions?.width - 40,
                                height: 120,
                                backgroundColor: theme.colors.white,
                                alignSelf: "center",
                                fontSize: 14,
                            }}
                            multiline={true}
                            blurOnSubmit={true}
                            onChangeAdditional={(value) => {
                                setFormValues({
                                    ...control?._formValues,
                                    instructions: value,
                                })
                            }}
                            onSubmitEditing={() => {
                                Keyboard.dismiss()
                            }}
                            control={control}
                            errors={errors}
                        />
                    </>
                )}
                {!isIndividual && control?._formValues?.type === "absence" && (
                    <Checkbox.Item
                        label={`Je confirme avoir rédigé un message d'excuse approprié pour les parents/élèves`}
                        position={"leading"}
                        onPress={() => {
                            parentNotificationValidation.onChange(
                                !parentNotificationValidation.value
                            )
                        }}
                        color={theme.colors.primary}
                        labelStyle={{
                            fontSize: 14,
                            textAlign: "left",
                        }}
                        style={{
                            marginVertical: 15,
                            padding: 0,
                            alignSelf: "flex-start",
                        }}
                        status={
                            parentNotificationValidation.value
                                ? "checked"
                                : "unchecked"
                        }
                    />
                )}
                <View
                    style={{
                        marginTop: 10,
                    }}
                >
                    {isIndividual ? (
                        <>
                            <TextM style={{ marginTop: 20 }}>
                                Ancien{" "}
                                {isOneOccurrence ? "RDV" : "créneau régulier"} :
                            </TextM>
                            <View
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    marginBottom: 20,
                                }}
                            >
                                <TextM>{"\u2022"}</TextM>
                                <TextM
                                    style={{
                                        textDecorationLine: "line-through",
                                        marginLeft: 5,
                                    }}
                                >
                                    {oldDate}
                                </TextM>
                            </View>
                            <TextM style={{}}>
                                Nouveau{" "}
                                {isOneOccurrence ? "RDV" : "créneau régulier"} :
                            </TextM>
                            <View
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    marginBottom: 20,
                                }}
                            >
                                <TextM fontWeight={"SemiBold"}>
                                    {"\u2022"}
                                </TextM>
                                <TextM
                                    style={{
                                        marginLeft: 5,
                                    }}
                                    fontWeight={"SemiBold"}
                                >
                                    {newDate}
                                </TextM>
                            </View>
                            <Checkbox.Item
                                label={`Je confirme que j'ai déjà validé ce changement avec ${firstStudent}, par message ou à l'oral`}
                                position={"leading"}
                                onPress={() => {
                                    if (!studentValidation.value)
                                        DialogM.show({
                                            text1: `Est-ce que tu confirmes que ${firstStudent} a déjà validé ce changement ?`,
                                            buttonText1: "Oui",
                                            buttonColor1: theme.colors.success,
                                            onPress: () => {
                                                studentValidation.onChange(
                                                    !studentValidation.value
                                                )
                                            },
                                            buttonText2: "Non",
                                        })
                                    else
                                        studentValidation.onChange(
                                            !studentValidation.value
                                        )
                                }}
                                color={theme.colors.primary}
                                labelStyle={{
                                    fontSize: 14,
                                    textAlign: "left",
                                }}
                                style={{
                                    marginVertical: 5,
                                    padding: 0,
                                    alignSelf: "flex-start",
                                }}
                                status={
                                    studentValidation.value
                                        ? "checked"
                                        : "unchecked"
                                }
                            />
                        </>
                    ) : (
                        <View
                            style={{
                                marginTop: 20,
                            }}
                        ></View>
                    )}
                </View>
                <View style={{ marginBottom: 60 }}>
                    <FilledButtonM
                        label={isFinalStep ? "Confirmer" : "Suivant"}
                        color={
                            cantContinue
                                ? theme.colors.greyAlt
                                : isFinalStep
                                ? theme.colors.success
                                : theme.colors.primary
                        }
                        style={{ marginBottom: 5, marginTop: 10 }}
                        disabled={cantContinue}
                        onPress={() => {
                            if (cantContinue) {
                                return
                            }

                            const RescheduledOccurrence =
                                liveSession?.next_occurrences?.find(
                                    (occ) => occ?.rescheduling_id
                                )
                            if (
                                isIndividual &&
                                !isOneOccurrence &&
                                !!RescheduledOccurrence
                            ) {
                                DialogM.show({
                                    text1:
                                        "Un live avait déjà été replanifié le " +
                                        moment(
                                            RescheduledOccurrence?.date
                                        ).format("dddd DD MMMM [à] HH:mm"),
                                    text2: "Veux-tu écraser ce changement ?",
                                    buttonText1: "Confirmer",
                                    buttonColor1: theme.colors.error,
                                    onPress: () => {
                                        goToNextStep()
                                    },
                                    buttonText2: "Annuler",
                                })
                            } else goToNextStep()
                        }}
                    />
                    {isIndividual && (
                        <TextM
                            style={{ textAlign: "center", marginBottom: 30 }}
                            color={theme.colors.light}
                            wrap
                        >
                            LES PARENTS SERONT NOTIFIÉS IMMÉDIATEMENT PAR SMS
                        </TextM>
                    )}
                </View>
            </ScrollView>
        </>
    )
}

const mapStateToProps = (state) => {
    return {
        rescheduling_reasons: state.data.staticData?.rescheduling_reasons,
        tutor_absence_reasons: state.data.staticData?.tutor_absence_reasons,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(MoveSessionReasonComponent)
